/**
 * Confirmation modal that fires when the React Router blocker arms during
 * navigation away from a dirty report. Owned by Report.tsx; receives the
 * blocker as a prop along with two callbacks:
 *
 *  - `onConfirmLeave`: snap the baseline + proceed (the order matters —
 *    flipping baseline first disarms the blocker before it can re-flash)
 *  - `onDontWarnAgain`: persist the "don't warn" preference. The parent
 *    routes this through SettingsContext so this component stays feature-pure.
 */

import { Modal, Checkbox } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';

interface Blocker {
  state: string;
  proceed?: () => void;
  reset?: () => void;
}

interface UnsavedChangesModalProps {
  blocker: Blocker;
  onConfirmLeave: () => void;
  onDontWarnAgain: (dontWarn: boolean) => void;
}

export function UnsavedChangesModal({
  blocker,
  onConfirmLeave,
  onDontWarnAgain,
}: UnsavedChangesModalProps) {
  return (
    <Modal
      title="Unsaved Changes"
      open={blocker.state === 'blocked'}
      onOk={onConfirmLeave}
      onCancel={() => blocker.reset?.()}
      okText="Leave"
      cancelText="Stay"
      okButtonProps={{ danger: true }}
    >
      <p>You have unsaved changes. Are you sure you want to leave?</p>
      <Checkbox
        onChange={(e: CheckboxChangeEvent) => onDontWarnAgain(e.target.checked)}
      >
        Don&apos;t warn about unsaved changes
      </Checkbox>
    </Modal>
  );
}
